"use client";

import { useState, useRef } from "react";
import { Container, Title, Text, Paper, Group, Button, Image, Stack, LoadingOverlay, Alert } from "@mantine/core";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { IconUpload, IconPhoto, IconX } from "@tabler/icons-react";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { notifications } from "@mantine/notifications";
import { v4 as uuidv4 } from "uuid";
import firebase_app from "@/firebase/config";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { trackCtaClick } from "@/lib/analytics";

export default function AgeGuesserContent() {
  const t = useTranslations("aiAgeGuesser");
  const [file, setFile] = useState<FileWithPath | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ age: number; justification: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasProcessedCurrentPhoto, setHasProcessedCurrentPhoto] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const handleDrop = (files: FileWithPath[]) => {
    const file = files[0];
    setFile(file);
    setPreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
    setHasProcessedCurrentPhoto(false);
  };

  const estimateAge = async () => {
    if (!file || hasProcessedCurrentPhoto) return;

    try {
      setLoading(true);
      setError(null);
      setHasProcessedCurrentPhoto(true);

      // Upload to Firebase Storage
      const storage = getStorage(firebase_app);
      const imageId = uuidv4();
      const storageRef = ref(storage, `ageGuesser/${imageId}`);
      await uploadBytes(storageRef, file);
      const isDev = process.env.NODE_ENV !== "production";

      // Call the cloud function with the image ID and type
      const functions = getFunctions(firebase_app);
      if (isDev) {
        connectFunctionsEmulator(functions, "localhost", 5001);
      }

      // Call the cloud function with the image ID
      const estimateAge = httpsCallable(functions, "estimateAge");
      const response = await estimateAge({
        imageId,
        fileType: file.type || "image/jpeg", // Fallback to jpeg if type is not available
      });

      setResult(response.data as { age: number; justification: string });

      // Scroll to result after a short delay to ensure it's rendered
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err: any) {
      console.error("Error estimating age:", err);
      setError("Failed to estimate age. Please try again.");
      notifications.show({
        title: "Error",
        message: "Failed to estimate age. Please try again.",
        color: "red",
      });
      setHasProcessedCurrentPhoto(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setHasProcessedCurrentPhoto(false);
  };

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Title order={1} ta="center">
          {t("title")}
        </Title>
        <Text size="lg" c="dimmed" ta="center">
          {t("description")}
        </Text>

        <Paper withBorder p="xl" radius="md" pos="relative">
          <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

          {error && (
            <Alert color="red" mb="md">
              {error}
            </Alert>
          )}

          {!preview ? (
            <Dropzone
              onDrop={handleDrop}
              onReject={(files) => console.log("rejected files", files)}
              maxSize={5 * 1024 ** 2}
              accept={{
                "image/*": [".png", ".jpg", ".jpeg", ".webp"],
              }}
            >
              <Group justify="center" gap="xl" mih={220} style={{ pointerEvents: "none" }}>
                <Dropzone.Accept>
                  <IconUpload
                    style={{
                      width: 52,
                      height: 52,
                      color: "var(--mantine-color-blue-6)",
                    }}
                    stroke={1.5}
                  />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX
                    style={{
                      width: 52,
                      height: 52,
                      color: "var(--mantine-color-red-6)",
                    }}
                    stroke={1.5}
                  />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconPhoto
                    style={{
                      width: 52,
                      height: 52,
                      color: "var(--mantine-color-dimmed)",
                    }}
                    stroke={1.5}
                  />
                </Dropzone.Idle>

                <div>
                  <Text size="xl" inline>
                    {t("dragPhoto")}
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={7}>
                    {t("uploadHint")}
                  </Text>
                </div>
              </Group>
            </Dropzone>
          ) : (
            <Stack align="center" gap="md">
              <Image
                src={preview}
                alt="Uploaded photo"
                radius="md"
                style={{ maxWidth: "100%", maxHeight: "400px", objectFit: "contain" }}
              />
              <Group>
                {!hasProcessedCurrentPhoto ? (
                  <Button onClick={estimateAge} disabled={loading}>
                    {t("guessButton")}
                  </Button>
                ) : (
                  <Button variant="light" onClick={handleTryAgain}>
                    {t("tryAgain")}
                  </Button>
                )}
              </Group>
            </Stack>
          )}
        </Paper>

        {result && (
          <div ref={resultRef}>
            <Paper withBorder p="xl" radius="md">
              <Stack gap="md">
                <Text size="xl" ta="center" fw={500}>
                  {t("estimatedAge", { age: result.age })}
                </Text>
                <Text c="dimmed">{result.justification}</Text>
              </Stack>
            </Paper>

            <Paper withBorder p="xl" radius="md" mt="xl">
              <Stack gap="sm" ta="center" align="center">
                <Text size="xl" fw={500}>
                  {t("bridgeHeading")}
                </Text>
                <Text c="dimmed">{t("bridgeBody")}</Text>
                <Button component={Link} href="/ai-color-analysis?src=bridge_ageguesser" onClick={() => trackCtaClick("bridge_ageguesser_ca")}>
                  {t("bridgeButton")}
                </Button>
              </Stack>
            </Paper>
          </div>
        )}
      </Stack>
    </Container>
  );
}
